import React, { useState, useRef } from 'react';
import ExcelJS from 'exceljs';

interface ImportPhoneNumbersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (phoneNumbers: string[]) => void;
}

export const ImportPhoneNumbersModal: React.FC<ImportPhoneNumbersModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const [uploadedNumbers, setUploadedNumbers] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const validatePhoneNumber = (number: string): boolean => {
    // Remove any spaces, dashes, or other formatting
    const cleaned = number.replace(/[\s\-\(\)]/g, '');
    
    // Check if it starts with 263 and is exactly 12 digits
    if (!cleaned.startsWith('263')) {
      return false;
    }
    
    // Check if it's exactly 12 digits
    if (cleaned.length !== 12 || !/^\d{12}$/.test(cleaned)) {
      return false;
    }
    
    return true;
  };

  const normalizePhoneNumber = (value: string | number): string | null => {
    if (value === null || value === undefined || value === '') return null;

    let stringValue = String(value).trim();

    // Handle scientific notation for numbers
    if (typeof value === 'number' && String(value).includes('e')) {
      stringValue = value.toFixed(0); // Convert to fixed-point notation
    }

    const hasPlus = stringValue.startsWith('+');
    let digitsOnly = stringValue.replace(/[^0-9]/g, ''); // Remove all non-digits

    if (hasPlus) {
      digitsOnly = '+' + digitsOnly; // Re-add the plus if it was there
    }

    // Remove leading zeros if not part of an international prefix (e.g., +263)
    if (!hasPlus && digitsOnly.length > 1 && digitsOnly.startsWith('0')) {
      digitsOnly = digitsOnly.substring(1);
    }

    // Minimum length check (e.g., 7 digits for local numbers, more for international)
    if (digitsOnly.length < 7) return null;

    return digitsOnly;
  };

  const extractPhoneNumbers = (content: string): string[] => {
    // Extract phone numbers from CSV content
    // Handle both comma-separated and newline-separated values
    const lines = content.split(/\r?\n/).filter(line => line.trim());
    const numbers: string[] = [];
    
    for (const line of lines) {
      // Split by comma, semicolon, or tab
      const values = line.split(/[,;\t]/).map(v => v.trim()).filter(v => v);
      
      for (const value of values) {
        const normalized = normalizePhoneNumber(value);
        if (normalized && validatePhoneNumber(normalized)) {
          // Remove + if present for consistency
          const cleaned = normalized.replace(/^\+/, '');
          if (!numbers.includes(cleaned)) {
            numbers.push(cleaned);
          }
        }
      }
    }
    
    return numbers;
  };

  const processCSVFile = (file: File) => {
    setError(null);
    setIsProcessing(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const numbers = extractPhoneNumbers(content);

        if (numbers.length === 0) {
          setError('No valid phone numbers found in the file. Please ensure phone numbers start with 263 and are exactly 12 digits (e.g., 263712345678).');
          setIsProcessing(false);
          return;
        }

        setUploadedNumbers(numbers);
        setIsProcessing(false);
      } catch (err) {
        setError('Error processing file. Please ensure it\'s a valid CSV file.');
        setIsProcessing(false);
      }
    };

    reader.onerror = () => {
      setError('Error reading file. Please try again.');
      setIsProcessing(false);
    };

    reader.readAsText(file);
  };

  const processExcelFile = async (file: File) => {
    setError(null);
    setIsProcessing(true);
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = e.target?.result as ArrayBuffer;
        if (!data) {
          setError('Error reading file. Please try again.');
          setIsProcessing(false);
          return;
        }

        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(data);
        const numbers: string[] = [];

        // Iterate through all worksheets
        workbook.worksheets.forEach((worksheet) => {
          // Iterate through all rows
          worksheet.eachRow((row, rowNumber) => {
            // Iterate through all cells in the row
            row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
              // Get cell value
              const cellValueRaw = cell.value;
              
              if (cellValueRaw !== null && cellValueRaw !== undefined) {
                let cellValue: string | number = '';
                
                // Handle different cell value types
                if (typeof cellValueRaw === 'object') {
                  // Rich text or formula result
                  if ('text' in cellValueRaw) {
                    cellValue = (cellValueRaw as any).text || '';
                  } else if ('result' in cellValueRaw) {
                    // Formula result
                    const result = (cellValueRaw as any).result;
                    if (typeof result === 'number') {
                      // Handle large numbers (phone numbers) without scientific notation
                      if (result > 1e10) {
                        cellValue = result.toFixed(0);
                      } else {
                        cellValue = result;
                      }
                    } else {
                      cellValue = String(result);
                    }
                  } else {
                    cellValue = String(cellValueRaw);
                  }
                } else if (typeof cellValueRaw === 'number') {
                  // Handle numbers, especially large ones (phone numbers)
                  if (cellValueRaw > 1e10) {
                    // Likely a phone number stored as number - convert to string preserving all digits
                    cellValue = cellValueRaw.toFixed(0);
                  } else {
                    cellValue = cellValueRaw;
                  }
                } else if (cellValueRaw && typeof cellValueRaw === 'object' && 'toISOString' in cellValueRaw && typeof (cellValueRaw as any).toISOString === 'function') {
                  cellValue = (cellValueRaw as Date).toISOString();
                } else {
                  cellValue = String(cellValueRaw);
                }
                
                const normalized = normalizePhoneNumber(cellValue);
                if (normalized && validatePhoneNumber(normalized)) {
                  // Remove + if present for consistency
                  const cleaned = normalized.replace(/^\+/, '');
                  if (!numbers.includes(cleaned)) {
                    numbers.push(cleaned);
                  }
                }
              }
            });
          });
        });

        const uniqueNumbers = Array.from(new Set<string>(numbers));

        if (uniqueNumbers.length === 0) {
          setError('No valid phone numbers found in the Excel file. Please ensure phone numbers start with 263 and are exactly 12 digits (e.g., 263712345678).');
          setIsProcessing(false);
          return;
        }

        setUploadedNumbers(uniqueNumbers);
        setIsProcessing(false);
      } catch (err) {
        console.error("Error processing Excel file:", err);
        setError('Error processing Excel file. Please ensure it\'s a valid Excel file and contains phone numbers.');
        setIsProcessing(false);
      }
    };

    reader.onerror = () => {
      setError('Error reading file. Please try again.');
      setIsProcessing(false);
    };

    reader.readAsArrayBuffer(file);
  };

  const processFile = (file: File) => {
    setError(null);
    setUploadedNumbers([]);
    
    const fileName = file.name.toLowerCase();
    
    if (fileName.endsWith('.csv')) {
      processCSVFile(file);
    } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
      processExcelFile(file);
    } else {
      setError('Please upload a valid CSV or Excel file (.csv, .xlsx, .xls)');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleSave = () => {
    if (uploadedNumbers.length > 0) {
      onSave(uploadedNumbers);
      setUploadedNumbers([]);
      setError(null);
      onClose();
    }
  };

  const handleClose = () => {
    setUploadedNumbers([]);
    setError(null);
    setIsDragging(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Import Phone Numbers</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Upload Area */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            isDragging ? 'border-[#FF5722] bg-orange-50' : 'border-gray-300'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".csv,.xlsx,.xls"
            className="hidden"
          />
          <div className="space-y-4">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <div className="text-sm text-gray-600">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="text-[#FF5722] hover:text-[#F4511E] font-medium underline"
                disabled={isProcessing}
              >
                Click to upload
              </button>
              {' or drag and drop'}
            </div>
            <p className="text-xs text-gray-500">
              CSV or Excel file with phone numbers (one per line or comma-separated)
            </p>
            {isProcessing && (
              <p className="text-sm text-[#FF5722]">Processing file...</p>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-md text-sm border border-red-200">
            {error}
          </div>
        )}

        {/* Preview Area */}
        {uploadedNumbers.length > 0 && (
          <div className="mt-6">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium text-gray-700">
                Found {uploadedNumbers.length} phone number(s)
              </h3>
            </div>
            <div className="border rounded-md p-4 bg-gray-50">
              <div className="space-y-2">
                {uploadedNumbers.slice(0, 5).map((number, index) => (
                  <div key={index} className="text-sm text-gray-600 bg-white p-2 rounded">
                    {number}
                  </div>
                ))}
                {uploadedNumbers.length > 5 && (
                  <div className="text-center py-2 text-sm text-gray-500 italic">
                    ... and {uploadedNumbers.length - 5} more number(s)
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 flex justify-end space-x-4">
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={uploadedNumbers.length === 0 || isProcessing}
            className={`px-4 py-2 rounded-md transition-colors ${
              uploadedNumbers.length > 0 && !isProcessing
                ? 'bg-[#FF5722] text-white hover:bg-[#F4511E]'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            Add {uploadedNumbers.length > 0 ? `${uploadedNumbers.length} ` : ''}Number{uploadedNumbers.length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

